---
title: "What Is The 2026 FIFA World Cup?"
slug: "what-is-the-2026-fifa-world-cup"
excerpt: "The 2026 FIFA World Cup represents a significant expansion in the tournament's history, increasing the number of participating teams from 32 to 48...."
publishedAt: "2026-05-25"
updatedAt: "2026-05-25"
author: "maya-lestari"
category: "Sports"
tags:
  - world cup
  - fifa
  - football
  - soccer
featuredImage: "https://img.bgstatic.com/multiLang/coinPriceLogo/fifa-world-cup-2026.jpg"
status: "published"
draft: false
---

## Short Answer

<ShortAnswer>
The 2026 FIFA World Cup represents a significant expansion in the tournament's history, increasing the number of participating teams from 32 to 48. This change was approved by FIFA in January 2017 to provide more opportunities for national teams from various confederations to compete at the highest level. The tournament will feature 104 matches, nearly double the number of games played in previous editions.
</ShortAnswer>


## Infobox

<InfoBox title="2026 FIFA World Cup">
<ul>
<li><strong>Tournament:</strong> 2026 FIFA World Cup</li>
<li><strong>Host countries:</strong> Canada, Mexico, United States</li>
<li><strong>Dates:</strong> June 11 – July 19, 2026</li>
<li><strong>Teams:</strong> 48 (from 6 confederations)</li>
<li><strong>Matches:</strong> 104</li>
<li><strong>Venues:</strong> 16 (across 16 host cities)</li>
<li><strong>Champion:</strong> TBD</li>
<li><strong>Top scorer:</strong> TBD</li>
<li><strong>Official song:</strong> TBD</li>
<li><strong>Website:</strong> FIFA.com</li>
</ul>
</InfoBox>

## Overview

The 2026 FIFA World Cup represents a significant expansion in the tournament's history, increasing the number of participating teams from 32 to 48. This change was approved by FIFA in January 2017 to provide more opportunities for national teams from various confederations to compete at the highest level. The tournament will feature 104 matches, nearly double the number of games played in previous editions.

The host countries—Canada, Mexico, and the United States—were announced as the successful bidders on June 13, 2018, following a vote by the FIFA Congress. The selection of these nations reflects a strategic decision to promote football in North America, where the sport has been growing rapidly. The tournament will be the first to be held in Canada since 2015 and the first in Mexico since 1986.

The 2026 World Cup will also introduce several innovations, including the use of semi-automated offside technology and video assistant referees (VAR) for all matches. Additionally, the tournament will emphasize sustainability, with host cities and stadiums required to meet strict environmental standards.

## History / Background

### Expansion to 48 teams

The decision to expand the 2026 FIFA World Cup to 48 teams was made during the FIFA Congress in January 2017. The proposal, put forward by then-FIFA President Gianni Infantino, aimed to increase the global reach of the tournament and provide more opportunities for teams from Africa, Asia, and North America. The expansion was controversial among some football purists, who argued that it could dilute the quality of competition. However, FIFA maintained that the move would strengthen the tournament's appeal and commercial viability.

The new format will see 16 groups of three teams, with the top two teams from each group advancing to a 32-team knockout stage. This structure ensures that all 48 teams will play a minimum of three matches, reducing the risk of early exits without playing a full slate of games.

### Host selection

The bidding process for the 2026 FIFA World Cup began in 2017, with several countries and joint bids submitting proposals. The final candidates were a joint bid from Canada, Mexico, and the United States, and a bid from Morocco. On June 13, 2018, the FIFA Congress voted to award the tournament to the North American bid with 134 votes to Morocco's 65. The selection was seen as a strategic move to grow football in new markets and capitalize on the existing infrastructure in the host countries.

The joint bid was praised for its strong financial backing, extensive stadium network, and experience in hosting major sporting events, including the FIFA World Cup in 1994 (United States), the FIFA Women's World Cup in 1999 and 2003 (United States), and the FIFA Confederations Cup in 2017 (Russia). The bid also highlighted the cultural diversity and passion for football in North America, where the sport has seen significant growth in recent years.

## How It Works

### Qualification

The qualification process for the 2026 FIFA World Cup will determine which 45 teams (in addition to the three host nations) will participate in the tournament. The allocation of spots by confederation is as follows:

- **AFC (Asia):** 8 slots
- **CAF (Africa):** 9 slots
- **CONCACAF (North and Central America and Caribbean):** 6 slots (3 for hosts)
- **CONMEBOL (South America):** 6 slots
- **OFC (Oceania):** 1 slot
- **UEFA (Europe):** 16 slots
 
The qualification process will begin in 2023 and conclude in early 2026. Each confederation will organize its own qualifying tournaments, with some confederations introducing new formats to accommodate the increased number of teams.

### Format

The 2026 FIFA World Cup will follow a new format designed to accommodate 48 teams. The tournament will consist of a group stage followed by a knockout stage:

1. **Group Stage:** 16 groups of three teams each. Each team will play two matches (one against each of the other teams in the group). The top two teams from each group will advance to the knockout stage.
2. **Knockout Stage:** A 32-team single-elimination tournament beginning with the Round of 32, followed by the Round of 16, Quarter-finals, Semi-finals, and the Final. In the event of a draw in the knockout stage, extra time and penalty shootouts will be used to determine the winner.
 
The new format ensures that all 48 teams will play a minimum of three matches, reducing the risk of early exits without playing a full slate of games. The knockout stage will feature 16 new matches compared to previous editions, increasing the total number of matches to 104.

### Venues and host cities

The 2026 FIFA World Cup will be hosted across 16 cities in three countries: Canada, Mexico, and the United States. The venues were selected based on their capacity, infrastructure, and ability to meet FIFA's stringent requirements for hosting a World Cup. The final will take place at MetLife Stadium in East Rutherford, New Jersey, United States.

The host cities and venues are as follows:

- **Canada:** Toronto (BMO Field), Vancouver (BC Place)
- **Mexico:** Guadalajara (Estadio Akron), Mexico City (Estadio Azteca), Monterrey (Estadio BBVA)
- **United States:**
 - Atlanta (Mercedes-Benz Stadium)
 - Boston (Gillette Stadium)
 - Dallas (AT&amp;T Stadium)
 - Houston (NRG Stadium)
 - Kansas City (Arrowhead Stadium)
 - Los Angeles (SoFi Stadium)
 - Miami (Hard Rock Stadium)
 - New York/New Jersey (MetLife Stadium)
 - Philadelphia (Lincoln Financial Field)
 - San Francisco Bay Area (Levi's Stadium)
 - Seattle (Lumen Field)

## Important Facts

<FactList>
<ul>
<li><strong>First 48-team World Cup:</strong> The 2026 tournament will be the first in FIFA World Cup history to feature 48 teams, nearly doubling the number of participants from previous editions.</li>
<li><strong>First multi-national World Cup:</strong> The tournament will be the first to be hosted by three countries: Canada, Mexico, and the United States.</li>
<li><strong>Longest tournament duration:</strong> The 2026 World Cup will run for 39 days, the longest duration in the tournament's history.</li>
<li><strong>Most matches:</strong> With 104 matches scheduled, the 2026 World Cup will feature nearly double the number of games compared to previous editions.</li>
<li><strong>Sustainability focus:</strong> The tournament will emphasize environmental sustainability, with host cities and stadiums required to meet strict green standards.</li>
<li><strong>Semi-automated offside technology:</strong> The 2026 World Cup will introduce semi-automated offside technology, designed to improve the accuracy and speed of offside decisions.</li>
<li><strong>Increased prize money:</strong> FIFA has announced a significant increase in prize money for the 2026 tournament, reflecting the expanded format and commercial growth.</li>
</ul>
</FactList>

## Timeline

<Timeline items={[
  { date: "January 2017", title: "FIFA approves expansion of", description: "FIFA approves expansion of the 2026 World Cup to 48 teams." },
  { date: "May 2017", title: "Bidding process for the", description: "Bidding process for the 2026 World Cup officially begins." },
  { date: "June 13, 2018", title: "Canada, Mexico, and the", description: "Canada, Mexico, and the United States are announced as the host countries." },
  { date: "2023", title: "Qualification process begins", description: "Qualification process begins." },
  { date: "Early 2026", title: "Qualification process conclude", description: "Qualification process concludes." },
  { date: "June 11, 2026", title: "Opening match of the", description: "Opening match of the 2026 FIFA World Cup." },
  { date: "July 19, 2026", title: "Final match of the", description: "Final match of the 2026 FIFA World Cup at MetLife Stadium." }
]} />

## Related Terms

<RelatedTerms>
<ul>
<li><strong>Semi</strong> – automated offside technology</li>
</ul>
</RelatedTerms>

## FAQ

<FAQ>
<details>
<summary>Why was the 2026 World Cup expanded to 48 teams?</summary>
<p>The expansion to 48 teams was approved by FIFA in 2017 to increase the global reach of the tournament, provide more opportunities for national teams from various confederations, and strengthen the commercial viability of the event. The move was also intended to promote football in new markets and capitalize on the growing popularity of the sport.</p>
</details>
<details>
<summary>How many matches will there be in the 2026 World Cup?</summary>
<p>The 2026 FIFA World Cup will feature 104 matches, nearly double the number of games played in previous editions. This includes 48 group-stage matches and 56 knockout-stage matches.</p>
</details>
<details>
<summary>Which countries are hosting the 2026 World Cup?</summary>
<p>The 2026 FIFA World Cup will be jointly hosted by three countries: Canada, Mexico, and the United States. This is the first time in history that the tournament will be held across three nations.</p>
</details>
<details>
<summary>Where will the final of the 2026 World Cup be played?</summary>
<p>The final of the 2026 FIFA World Cup will be played at MetLife Stadium in East Rutherford, New Jersey, United States.</p>
</details>
<details>
<summary>How many teams will qualify for the 2026 World Cup?</summary>
<p>A total of 48 teams will participate in the 2026 FIFA World Cup, including the three host nations (Canada, Mexico, and the United States). The remaining 45 teams will qualify through regional tournaments organized by FIFA's six confederations.</p>
</details>
<details>
<summary>What new technologies will be used in the 2026 World Cup?</summary>
<p>The 2026 FIFA World Cup will introduce several technological innovations, including semi-automated offside technology and video assistant referees (VAR) for all matches. These technologies are designed to improve the accuracy and speed of decision-making on the field.</p>
</details>
</FAQ>

## References

<ReferenceList>
<ol>
<li>FIFA. *FIFA approves 48-team World Cup from 2026*. January 10, 2017.</li>
<li>FIFA. *Canada, Mexico and USA chosen to host FIFA World Cup 2026™*. June 13, 2018.</li>
<li>FIFA. *FIFA World Cup 2026™ – Host Cities and Venues*.</li>
<li>FIFA. *FIFA World Cup 2026™ – Format and Regulations*.</li>
<li>FIFA. *FIFA World Cup 2026™ – Sustainability Strategy*.</li>
</ol>
</ReferenceList>

